import { Injectable } from '@angular/core';
import { CoreServiceRegistry } from '../../../../core/core-service-registry';
import { SharedServiceRegistry } from '../../../shared-service-registry';
import { SearchableEntityActionService } from '../searchable-entity-action.service';
import { DetailedEntityDataService } from './detailed-entity-data.service';
import { DetailedEntityButtonService } from './detailed-entity-button.service';
import { DetailedEntityDataEditorService } from './detailed-entity-data-editor.service';
import { DetailedEntityBackendService } from './detailed-entity-backend.service';
import { Observable } from 'rxjs/Observable';
import { ValidatorService } from '../../validator.service';
import { SearchService } from '../../../search/search.service';
import { SearchEvent } from '../../../search/models/search-event';
import { NEW_SEARCH_EVENT, REFRESH_SEARCH_EVENT } from '../../../search/search-constants';
import { menuIdRouteMappings } from '../../../../constants';
import { SearchParams } from '../../../search/models/search-params.model';
import { Condition } from '../../../search/models/search-filter-model';

@Injectable()
export class DetailedEntityActionService extends SearchableEntityActionService {

  protected dataService: DetailedEntityDataService;
  protected buttonService: DetailedEntityButtonService;
  protected backendService: DetailedEntityBackendService;
  protected dataEditor: DetailedEntityDataEditorService;
  protected validatorService: ValidatorService;


  constructor(protected coreServiceRegistry: CoreServiceRegistry,
    protected sharedServiceRegistry: SharedServiceRegistry) {
    super(coreServiceRegistry, sharedServiceRegistry);
  }
  // Method used to edit the Entity detail page
  public editPage() {
    this.dataService.setOriginalDataMap();
    this.dataEditor.populateDropdownValuesBasedOnStorerInAccordion();
    this.dataEditor.populateDropdownValuesBasedOnStorerInTables();
    if (this.dataService.displayMode === 'V') {
      this.dataService.setDisplayMode('E');
      this.buttonService.initializeButtons(this.dataService.primaryDataMap);

    } else if (this.dataService.displayMode === 'E') {
      this.dataService.setDisplayMode('V');
      this.buttonService.initializeButtons(this.dataService.primaryDataMap);
      this.clearEditedFlags();
    }
  }
  // Method used to save an entity
  public savePage(): Observable<any> {
    let entityDetails: Array<any>;
    let lineItems: Array<any>;
    let requestLineItems: Array<any>; // stores the line items to be sent for saving
    let requestEntityItems: Array<any>; // stores the entity details for saving
    
    if (this.dataService.displayMode === 'C') {
      // set the entity properties
      entityDetails = [];
      const entityData = Object.keys(this.dataService.primaryDataMap);
      entityData.forEach((property: string) => {
        entityDetails.push({ cName: property, cVal: this.dataService.primaryDataMap[property].cVal });
      });
      // Validation of primary id of entity details starts
      if (entityDetails.length !== 0) {
        for (let i = 0; i < entityDetails.length; i++) {
          if ((entityDetails[i].cName === this.dataService.entityConfig.getPrimarySearchParams().idProperty) && !entityDetails[i].cVal) {
            const propertyKey = entityDetails[i].cName;
            const property = this.resourceService.get(this.dataService.primaryMetaDataMap[propertyKey].elementId);
            this.alertService.error(this.dataService.createAlertMessage(this.resourceService.get('err_msg_val_prop'), property));
            return;
          }
        }
      }
      let invalidErrorMsg: any;
      invalidErrorMsg = this.validateEntityDetails(entityDetails);
      if (invalidErrorMsg) {
        this.errorMsg = invalidErrorMsg;
        this.alertService.clearAll().error(this.resourceService.get(this.errorMsg));
        return;
      }
      // Validation of primary id of entity details ends

      entityDetails.push({ cName: 'isNewItem', cVal: true });
      // If there are line items in details page
      if (this.dataService.entityConfig.getShowDetailLineItems()) {
        // set the line item properties
        lineItems = this.dataService.getAllLineItems(this.dataService.primaryDataMap);
      }
    } else if (this.dataService.displayMode === 'E') {
      // set the entity properties
      entityDetails = this.dataService.getModifiedPrimaryProperties(this.dataService.primaryDataMap);
      // Validation of entity details starts
      if (entityDetails.length !== 0) {
        const invalidErrorMsg = this.validateEntityDetails(entityDetails);
        if (invalidErrorMsg) {
          this.errorMsg = invalidErrorMsg;
          this.alertService.clearAll().error(this.resourceService.get(this.errorMsg));
          return;
        }
      }
      // Validation of entity details ends


      // If there are line items in details page
      if (this.dataService.entityConfig.getShowDetailLineItems()) {
        // set the line item properties
        lineItems = this.dataService.getModifiedLineItems(this.dataService.primaryDataMap);
        // adding the warning so as to avoid saving when no modifications are done to the entity and trying to save.
        if (entityDetails.length === 0 && lineItems.length === 0) {
          this.errorMsg = 'msg_no_mod';
          this.alertService.clearAll().warn(this.resourceService.get(this.errorMsg));
          this.dataService.getDetailsSearchService().clearMarkerFlags();
          this.clearEditedFlags();
          this.navService.preventNavigation = false;
          this.dataService.setDisplayMode('V');
          this.buttonService.initializeButtons(this.dataService.primaryDataMap);
          return;
        }
      } else {
        if (entityDetails.length === 0) {
          this.errorMsg = 'msg_no_mod';
          this.alertService.clearAll().warn(this.resourceService.get(this.errorMsg));
          this.clearEditedFlags();
          this.navService.preventNavigation = false;
          this.dataService.setDisplayMode('V');
          this.buttonService.initializeButtons(this.dataService.primaryDataMap);
          return;
        }
      }
    }

    if (lineItems && lineItems.length > 0) {
      lineItems.forEach(lineItem => {
        const cellProperities = Object.keys(lineItem.cells);
        for (let index = 0; index < cellProperities.length; index++) {
          const cell = lineItem.cells[index];
          if (cell.cName === 'listname' || cell.cName === 'containerKey') {
            cell.cVal = this.dataService.primaryDataMap[cell.cName].cVal;         
            return false;
          }    
        }
      });  
    }

    // If there are line items in details page
    if (this.dataService.entityConfig.getShowDetailLineItems()) {
      const errorIfAny = this.validateLineItems(lineItems);
      if (errorIfAny) {
        this.errorMsg = errorIfAny;
        this.alertService.clearAll().error(this.resourceService.get(this.errorMsg));
        return;
      }
    }
    requestEntityItems = this.getEntityValuesList();
    if (this.dataService.entityConfig.getShowDetailLineItems()) {
      requestLineItems = this.getLineItemsList(lineItems);
    }
    // added to validate the data entered by the user

    if (this.validateUserEnteredData()) {
      if (this.dataService.displayMode === 'C') {
        return this.backendService.saveEntityforAdd(this.dataService.getPrimaryDataIdValue(), requestEntityItems, requestLineItems)
          .map((response) => {
            return response;
          });
      } else if (this.dataService.displayMode === 'E') {
        return this.backendService.saveEntityforUpdate(this.dataService.getPrimaryDataIdValue(), requestEntityItems, requestLineItems)
          .map((response) => {
            return response;
          });
      }

    }
  }

  /*this method forms the request for saving line items. If the line item is an existing one while sending it for saving,
  the request should contain the original data as well as the edited data.
  The original data will be prefixed with _original.
  If it is a new line item the original prefix need not be appended
 */
  public getLineItemsList(modifiedLineItems) {
    const requestList: Array<any> = []; // stores the formatted line items
    let selectedLineItem;
    modifiedLineItems.forEach((row: any, index: any) => {
      selectedLineItem = [];
      let isNewItem = false;
      (row.cells).forEach((cell: any) => {
        if (cell.cName === 'isNewItem' && cell.cVal) {
          isNewItem = true;
        }
      });
      // const isNewItem: boolean = (row.cells['isNewItem'] && row.cells['isNewItem'].cVal) ? true : false;
      let typeName = '';
      if (!isNewItem) {
        typeName = 'original_';
        (row.cells).forEach((cell: any) => {
          if (cell.cName !== 'isNewItem') {
            if ((cell.cValOrig !== undefined) && (cell.cValOrig !== cell.cVal)) { // for fields which are edited
              // [LFWM-2408] - no need to send addwho and editWho this will be added at backend
              if (cell.cName.toLowerCase() === 'addwho' || cell.cName.toLowerCase() === 'editwho') {
                return false;
              } else {
              selectedLineItem.push({ cName: typeName + cell.cName, cVal: cell.cValOrig });
              selectedLineItem.push({ cName: cell.cName, cVal: cell.cVal });
              }
            } else { // for fields which are not edited
              // [LFWM-2408] - no need to send addwho and editWho this will be added at backend
              if (cell.cName.toLowerCase() === 'addwho' || cell.cName.toLowerCase() === 'editwho') {
                return false;
              } else {
              selectedLineItem.push({ cName: typeName + cell.cName, cVal: cell.cVal });
              selectedLineItem.push({ cName: cell.cName, cVal: cell.cVal });
              }
            }
          } else { // for new line items
            selectedLineItem.push({ cName: cell.cName, cVal: cell.cVal });
          }
        });
      } else {
            const strategyKeys = [
              'allocateStrategyKey',
              ' appntStrtgyKey',
              'doorBkingStrtgyKey',
              'preallocateStrategyKey',
              'putawayStrategyKey',
              'replenishStrategyKey',
              'ttmStrategyKey',
              'xdockStrategyKey',
              'holidayKey'];      
      
        (row.cells).forEach((cell: any) => {
          if (this.dataService.primarySearchParams.tableId === 'pmmanagesearchtd' && cell.cName === 'status') {
            const statusValue = cell.cVal === 'open' ? '0' : cell.cVal;
            selectedLineItem.push({ cName: cell.cName, cVal: statusValue });
          }
          else if (strategyKeys.includes(cell.cName)) { // Check if cell.cName exists in the array
            selectedLineItem.push({ cName: cell.cName, cVal: this.dataService.primaryDataMap[cell.cName].cVal });
          } else {
            selectedLineItem.push({ cName: cell.cName, cVal: cell.cVal });
          }
        });
      }
      requestList.push({ rIdx: index, cells: selectedLineItem });
    });
    return requestList;
  }

  /*This method forms the request for saving the entity details. While editing an existing entity,
  the original data should also be sent prefixed with _original, along with the edited data
  If it is a new entity the prefix can be avoided*/
  public getEntityValuesList() {
    let data;
    let initialData;
    const accordionDetails = [];
    const typeName = 'original_';
    Object.keys(this.dataService.primaryDataMap).forEach((property: any) => {
      data = this.dataService.primaryDataMap[property];
      initialData=this.dataService.originalDataMap[property];
      if (this.dataService.displayMode === 'E') {
        if (data.isEdited) {// for fields which are edited
          /*LFWM-1733 this.dataService.originalDataMap[property].cVal will give the original value
          of that particular field, cValPrev will give the previosly modified value and
          not the original value.The original value should also be send along with the req */
          // LFWM-1761 : Code fix
          // [LFWM-2408] - no need to send addwho and editWho this will be added at backend
          if (property.toLowerCase() === 'addwho' || property.toLowerCase() === 'editwho') {
            return false;
          } else {
            accordionDetails.push({ cName: typeName + data.cName, cVal: initialData.cVal });
            accordionDetails.push({ cName: data.cName, cVal: data.cVal });
          }
        } else {
          if (!(this.dataService.primaryMetaDataMap[data.cName] &&
            (this.dataService.primaryMetaDataMap[data.cName].displayType === 'H') && (data.cVal === null || data.cVal === undefined))) {
           //LFWM-2812 value of editwho in orderparameter screen should pass as empty, actual value will be updated from backend
            if (this.dataService.primarySearchParams && this.dataService.primarySearchParams.tableId === 'buildparmconfigtd' && property.toLowerCase() === 'editwho') {
              accordionDetails.push({ cName: typeName + data.cName, cVal: data.cVal });
              accordionDetails.push({ cName: data.cName, cVal: "" });
            } else {
              // [LFWM-2408] - no need to send addwho and editWho this will be added at backend
              if (property.toLowerCase() === 'addwho' || property.toLowerCase() === 'editwho') {
                return false;
              } else {
                accordionDetails.push({ cName: typeName + data.cName, cVal: data.cVal });
                accordionDetails.push({ cName: data.cName, cVal: data.cVal });
              }
            }
          }
        }
      } else { // for new entity
        accordionDetails.push({ cName: data.cName, cVal: data.cVal });
      }
    });
    if (this.dataService.displayMode === 'C') {
      accordionDetails.push({ cName: 'isNewItem', cVal: true });
    }
    return accordionDetails;
  }

  // Method used to delete the selected Entity
  public delete() {
    const isNewItem = this.dataService.getPrimarySearchService().getDisplayMode() === 'C'
      && this.dataService.getDetailsSearchService().getDisplayMode() === 'C';
    if (isNewItem) {
      this.alertService.clearAll().success(this.resourceService.get(this.successMsg));
      this.navService.retainAlerts().navigateByUrl(this.dataService.getEntityConfiguration().getSearchPageRoute());
    } else {
      this.confirmDialogService.confirmDialog(this.resourceService.get(this.dialogMsg))
        .subscribe((isConfirmed: any) => {
          if (isConfirmed) {
            const itemNumber = this.dataService.getPrimaryDataIdValue();
            this.backendService.deleteEntity(itemNumber)
              .subscribe((resp: any) => {
                if (resp['statusCode'] && resp['statusMessage']) {
                  if (resp['statusCode'] === 200 && resp['statusMessage'] === 'SUCCESS') {
                    // reload the search page
                    this.alertService.clearAll();
                    this.alertService.success(this.resourceService.get(this.successMsg));
                    this.navService.preventNavigation = false;
                    this.navService.removeLastBreadCrumbItem();
                    this.navService.retainAlerts().navigateByUrl(this.dataService.getEntityConfiguration().getSearchPageRoute());
                  } else if (resp['statusCode'] === 500) {
                    this.alertService.clearAll();
                    this.alertService.error(this.resourceService.translateServerResponse(resp['statusMessage']));
                  }
                } else {
                  if (resp.error) {
                    this.alertService.clearAll();
                    this.alertService.error(this.resourceService.translateServerResponse(resp.error));
                  }
                }
              }, (error) => {
                this.alertService.clearAll().error(this.resourceService.translateServerResponse(error));
              });
          }
        });
    }
  }

  // Method used to delete the selected Line Items
  public deleteLineItem() {
    const noOfLineItems = this.dataService.getDetailsSearchService().getPagedData().pageDefinition.recordCount;
    const selectedRows = this.dataService.getDetailsSearchService().getSelectedRows();
    if (noOfLineItems === 0) {
      this.errorMsg = 'msg_no_detail';
      this.alertService.clearAll().error(this.resourceService.get(this.errorMsg));
      return;
    }
    const lineItemProp = this.dataService.detailsSearchParams.idProperty;
    if (selectedRows.length > 0) {
      this.confirmDialogService.confirmDialog(this.resourceService.get(this.dialogMsg))
        .subscribe((isConfirmed: any) => {
          if (isConfirmed) {
            const itemNumber = this.dataService.getPrimaryDataIdValue();
            const selectedRow = this.dataService.getDetailsSearchService().getSelectedRows();
            const existingLineItemNumbers: Array<any> = [];
            const newLineItemNumbers: Array<string> = [];
            selectedRow.forEach((row: any) => {
              if (row.cells['isNewItem'] && row.cells['isNewItem'].cVal === true) {
                newLineItemNumbers.push(row.cells[lineItemProp].cVal);
              } else {
                existingLineItemNumbers.push({
                  'value': row.cells[lineItemProp].cVal,
                  'index': row.rIdx
                });
              }
            });
            if (newLineItemNumbers.length > 0) {
              this.dataService.getDetailsSearchService().deleteRowsFromModel(lineItemProp, newLineItemNumbers);
              this.buttonService.initializeButtons(this.dataService.primaryDataMap);
            }
            if (existingLineItemNumbers.length > 0) {
              this.backendService.deleteSelectedLineItemFromEntity(itemNumber, existingLineItemNumbers)
                .subscribe((resp: any) => {
                  if (resp['statusCode'] !== undefined && resp['statusCode'] !== null) {
                    if (resp['statusCode'] === 200 && resp['statusMessage'] === 'SUCCESS') {
                      // code to reload the  detail grid
                      this.alertService.clearAll();
                      this.alertService.success(this.resourceService.get(this.successMsg));
                      this.dataService.reloadDetailTable();
                    } else {
                      this.alertService.clearAll();
                      this.alertService.error(this.resourceService.translateServerResponse(resp.statusMessage));
                    }
                  } else {
                    if (resp.error) {
                      this.alertService.clearAll();
                      this.alertService.error(this.resourceService.translateServerResponse(resp.statusMessage));
                    }
                  }
                }, (error) => {
                  this.alertService.clearAll().error(this.resourceService.translateServerResponse(error));
                });
            }
          }
        });
    } else {
      this.alertService.clearAll();
      this.alertService.error(this.resourceService.get(this.errorMsg));
    }
  }

  // Method used to delete all the Line Items
  public deleteAllLineItems() {
    const noOfLineItems = this.dataService.getDetailsSearchService().getPagedData().pageDefinition.recordCount;
    const selectedRow = this.dataService.getDetailsSearchService().getPagedData().pageRows;
    if (noOfLineItems > 0) {
      this.confirmDialogService.confirmDialog(this.resourceService.get(this.dialogMsg))
        .subscribe((isConfirmed: any) => {
          if (isConfirmed) {
            const lineItemProp = this.dataService.detailsSearchParams.idProperty;
            const newLineItemNumbers: Array<string> = [];
            let hasExistingLineItems = false;
            selectedRow.forEach((row: any) => {
              if (row.cells['isNewItem'] && row.cells['isNewItem'].cVal === true) {
                newLineItemNumbers.push(row.cells[lineItemProp].cVal);
              } else {
                hasExistingLineItems = true;
              }
            });

            if (newLineItemNumbers.length > 0) {
              this.dataService.getDetailsSearchService().deleteRowsFromModel(lineItemProp, newLineItemNumbers);
              this.buttonService.initializeButtons(this.dataService.primaryDataMap);
            }

            if (hasExistingLineItems) {
              const entityNumber = this.dataService.getPrimaryDataIdValue();
              this.backendService.deleteAllLineItemsFromEntity(entityNumber)
                .subscribe((resp: any) => {
                  if (resp['statusCode'] !== undefined && resp['statusCode'] !== null) {
                    if (resp['statusCode'] === 200 && resp['statusMessage'] === 'SUCCESS') {
                      // code to reload the detail grid
                      this.alertService.clearAll();
                      this.alertService.success(this.resourceService.get(this.successMsg));
                      this.dataService.reloadDetailTable();
                    } else {
                      this.alertService.clearAll();
                      this.alertService.error(this.resourceService.translateServerResponse(resp.statusMessage));
                    }
                  } else {
                    if (resp.error) {
                      this.alertService.clearAll();
                      this.alertService.error(this.resourceService.translateServerResponse(resp.error));
                    }
                  }
                }, (error) => {
                  this.alertService.clearAll().error(this.resourceService.translateServerResponse(error));
                });
            }
          }
        });
    } else {
      this.alertService.clearAll().error(this.resourceService.get(this.errorMsg));
    }
  }

  // method used to clear all the edited flags
  /* LFWM-960 Removing the previous skip first clearing code */
  public clearEditedFlags() {
    Object.keys(this.dataService.primaryDataMap).forEach((key: any) => {
      this.dataService.primaryDataMap[key].isEdited = false;
    });
  }

  // method used to navigate to the details page
  public showDetailsPage() {
    this.dataService.setDisplayMode('C');
    this.navService.retainAlerts().navigateByUrl(this.dataService.getEntityConfiguration().getDetailPageRoute());
  }

  // method used to validate the line items
  public validateLineItems(lineItems) {
    return null;
  }

  // method used to validate the entity details
  public validateEntityDetails(entityDetails) {
    return null;
  }
  public validateUserEnteredData() { // while over writing this method these two validations should madatorily be performed
    // to validate accordion details
    const validatePrimaryDetailsErr = this.validatorService.validateRow(this.dataService.getPrimarySearchService().
      getMetadata(), this.dataService.primaryDataMap);
    // added to validate the fields of line items if they are present
    if (this.dataService.entityConfig.getShowDetailLineItems()) {
      const validateLineItemsErr = this.validatorService.validateMultipleRows(this.dataService.getDetailsSearchService().
        getMetadata(), this.dataService.getDetailsSearchService().getPagedData());
      if (validateLineItemsErr || validatePrimaryDetailsErr) {
        return false;
      } else {
        return true;
      }
    } else { // for screens without line items
      if (validatePrimaryDetailsErr) {
        return false;
      } else {
        return true;
      }
    }
  }

  // [LFWM-2125] - to get dynamic buttons based on module name and storerKey
  public getDynamicButtons(): Observable<any> {
    const storerKey = this.dataService.primaryDataMap['storerKey'].cVal;
    const moduleName = this.dataService.tableIdToModuleNameMapping[this.dataService.primarySearchParams.tableId];
    // LFWm-3191- adding tableId in the request.
    const tableId = this.dataService.primarySearchParams.tableId;
    return this.backendService.getDynamicButtons(storerKey, moduleName, tableId).map((resp: any) => {
      return resp;
    });
  }

  // [LFWM-2125] - method to handle dynamic button actions
  // [LFWM-3134] - added storerKeyVal optional param to pass specific storerKey.
  public handlingDynamicButtonAction(action: string, storerKeyVal?: string) {
    const currentButtonInfo = this.buttonService.dynamicButtons.filter(button =>
      button.staticButtonName.toUpperCase() === action.toUpperCase());
    const confirmationMsg = currentButtonInfo[0]['confirmationMsg'] ? currentButtonInfo[0]['confirmationMsg'] :
      this.resourceService.get('genric_confirm_msg');
    this.confirmDialogService.confirmDialog(confirmationMsg)
      .subscribe((isConfirmed: any) => {
        if (isConfirmed) {
          const storerKey = storerKeyVal || this.dataService.primaryDataMap['storerKey'].cVal;
          const documentKey = this.dataService.getPrimaryDataIdValue();
          const moduleName = this.dataService.tableIdToModuleNameMapping[this.dataService.primarySearchParams.tableId];
          const actionCode = currentButtonInfo[0]['code'];
          // LFWM-3191-adding tableId Nand screenTypr to request.
          const tableId = this.dataService.primarySearchParams.tableId;
          const screenType = action;
          this.backendService.handlingDynamicButtonAction(storerKey, documentKey, moduleName, actionCode, tableId, screenType).subscribe((resp: any) => {
            if (resp.statusCode === 200) {
              this.alertService.clearAll();
              //LFWM-3611 We will get two messages from backend in case of success and have to display both based on condition.
              if (resp.statusMessage === 'SUCCESS') {
                this.alertService.success(this.dataService.createAlertMessage(this.resourceService.get('msg_activity_suc'), currentButtonInfo[0]['buttonName']));
              } else {
                this.alertService.success(resp.statusMessage);
              }
               // [LFWMS - 3078] calling LoadByPage after dynamic button action
              this.refreshAccordion();
              this.dataService.reloadDetailTable();
            } else {
              if (resp.errMsg) {
                this.alertService.clearAll().error(this.resourceService.translateServerResponse(resp.errMsg));
              }
            }
          }, (error) => {
            this.alertService.clearAll().error(this.resourceService.get(error));
          });
        }
      });
  }

  // [LFWM-2132] - method used to save the edited rows search table
  public saveSearchTableRows(): Observable<any> {
    // to get modified/edited rows from search table
    const lineItems = this.dataService.getModifiedLineItemsOfSearchTable();
    if (lineItems.length === 0) {
      this.errorMsg = 'msg_no_mod';
      this.alertService.clearAll().warn(this.resourceService.get(this.errorMsg));
      this.dataService.getPrimarySearchService().clearMarkerFlags();
      this.navService.preventNavigation = false;
      this.dataService.setDisplayMode('V');
      this.buttonService.initializeSearchPageButtons(this.dataService.primaryDataMap);
      return;
    }
    // format the modified line items in the required request format
    const requestItems = this.getCellValuesList(lineItems);
    return this.backendService.saveEntityDetails(requestItems).map((response) => {
      return response;
    });
  }

  // [LFWM-2132] - method to format the line items of search table in required format
  public getCellValuesList(modifiedLineItems: any) {
    const requestList: Array<any> = [];
    let selectedLineItem;
    modifiedLineItems.forEach((row: any, index: any) => {
      selectedLineItem = [];
      const typeName = 'original_';
      row.forEach((cell: any) => {
        if ((cell.cValOrig !== undefined) && (cell.cValOrig !== cell.cVal)) {
          // [LFWM-2408] - no need to send addwho and editWho this will be added at backend
          if (cell.cName.toLowerCase() === 'addwho' || cell.cName.toLowerCase() === 'editwho') {
            return false;
          } else {
          selectedLineItem.push({ cName: typeName + cell.cName, cVal: cell.cValOrig });
          selectedLineItem.push({ cName: cell.cName, cVal: cell.cVal });
          }
        } else {
          // [LFWM-2408] - no need to send addwho and editWho this will be added at backend
          if (cell.cName.toLowerCase() === 'addwho' || cell.cName.toLowerCase() === 'editwho') {
            return false;
          } else {
          selectedLineItem.push({ cName: typeName + cell.cName, cVal: cell.cVal });
          }
        }
      });
      const request = {
        'moduleProperties': selectedLineItem,
        'screenType': this.dataService.primarySearchParams.tableId
      };
      requestList.push(request);
    });
    return requestList;
  }

  // method to prepare request without additional changes to the list
  public getCellValuesListWithoutChanges(modifiedLineItems: any) {
    const requestList: Array<any> = [];
    let selectedLineItem;
    modifiedLineItems.forEach((row: any, index: any) => {
      selectedLineItem = [];
      row.forEach((cell: any) => {
          selectedLineItem.push({ cName: cell.cName, cVal: cell.cVal });
      });
      const request = {
        'moduleProperties': selectedLineItem,
        'screenType': this.dataService.primarySearchParams.tableId
      };
      requestList.push(request);
    });
    return requestList;
  }

  /* Method used to copy value to all rows in both search and detail table
  * from search table - pass PrimarySearchService as input
  * from detail table - pass DetailSearchService as input
  */
  public copyValueToAllRows(searchService: SearchService) {
    //all rows that are selected in the table
    let selectedColumnToCopy = '';

    const msgNoFocusRow: string = 'msg_clik_cell_to_copy_value';
    const msgCantCopyField: string = 'msg_can_not_copy_field';

    if (searchService.getAllRows().length === 0) {
      this.alertService.clearAll().error(this.resourceService.get(this.errorMsg));
      return false;
    }

    if (searchService.getSelectedRows().length === 0) {
      this.alertService.clearAll().error(this.resourceService.get('msg_no_row_selected'));
      return false;
    }
    
    if (!searchService.getFocusedRow() || !searchService.getFocusedRow().cName || searchService.getFocusedRow().cName === 'isSelected' ) {
      this.alertService.clearAll().error(this.resourceService.get(msgNoFocusRow));
      return false;
    }
    
    if (searchService.getFocusedRow().cName) {
      selectedColumnToCopy = searchService.getFocusedRow().cName;
    }

    const displayTypeSelectedField = searchService.getMetadataForProperty(selectedColumnToCopy).displayType;
    //  Non editable field values should not be allowed to copy as they are set from other editable fields
    if (displayTypeSelectedField === 'V') {
      this.alertService.clearAll().error(this.resourceService.get(msgCantCopyField));
      return false;
    }

    let valueToCopy = '';
    if (searchService.getFocusedRow().row) {
      valueToCopy = searchService.getFocusedRow().row.cells[selectedColumnToCopy].cVal;
    }

    this.dialogMsg = this.resourceService.get('msg_copy_colmn') + this.resourceService.get(selectedColumnToCopy)
      + ` ${this.resourceService.get('msg_to_all_selected_row')} ` + this.resourceService.get(selectedColumnToCopy)
      + this.resourceService.get('msg_overwrt');
    this.confirmDialogService.confirmDialog(this.resourceService.get(this.dialogMsg))
      .subscribe((isConfirmed: any) => {
        if (isConfirmed) {
          // code to copy the selected row value to all rows
          searchService.getSelectedRows().forEach((lineItemRows: any) => {
            const lineItemCells = lineItemRows.cells;
            if(lineItemCells.externasnStatus && lineItemCells.externasnStatus.cValOrig === '9'){
              return;
            } else if(lineItemCells.containerKey && lineItemCells.status && lineItemCells.status.cValOrig === '9'){
              return;
            }
            lineItemCells[selectedColumnToCopy].cVal = valueToCopy;
            // LFWM - 3792 copy Value To Rows not working for received Qty/ Expected Qty
            lineItemCells[selectedColumnToCopy]['isEdited'] = true;
            lineItemRows.isEdited = true;
          });
          searchService.publishEvent(new SearchEvent(REFRESH_SEARCH_EVENT));
        }
      });
  }
   /*LFWM-2240- LF SCE JReport Integration Phase 2 */
  public jReportSubmit(data, tableSearchService?: any) {
    const tableSearchServiceForWaveControl = tableSearchService;
    const dialogMsg1: string = `<b>` + this.resourceService.get('mn_data_lost_msg') + `</b>`;
    const dialogMsg2: string = this.resourceService.get('mn_nav_confirm_msg');
    const dialogMsgs: Array<any> = [];
    dialogMsgs.push(dialogMsg1);
    dialogMsgs.push(dialogMsg2);
       
   /* ---------------------
   Inbound Module
   ----------------------
      PO - potd 
      ASN- asntd
      Trade Return - trtd  
      XDock - ixdock 
    -------------------------   
    Outbound Module
    -------------------------  
      Shipment Order - sotd 
      XDock - oxdock
      Shipment Reference - ship_ref 
      Wave Control - wave_ctrl

    --------------------------
    Inventory VAS
    -------------------------
      Transfer – vas_trnsfr
      VAS Work Order
      Kitting
      Inventory QC
    -------------------------
    Inventory Adjustment
    -------------------------
      UCC Adjustment
      Inventory Replenishment 
    ------------------------
    Inventory Cycle Count
    ------------------------
      Stock Take Parameters
   */
      
    const menuIdTableMapping  = { 'asntd': 'asn',
                                  'trtd':  'trade-returns',
                                  'potd' : 'po',
                                  'oxdocktd': 'oxdock',
                                  'sotd': 'so',
                                  'ixdocktd': 'ixdock',
                                  'shipreftd':'ship_ref',
                                  'transfertd':'vas_trnsfr',
                                  'wotd':'vas_wo',
                                  'inventoryqctd' :'inv_qc',
                                  'stktaketd': 'stktake_params',
                                  'wavetabtd': 'wave_ctrl',
                                  'kittd': 'kitting',
                                  'adjtd': 'inv_adj',
                                  'uccadjtd': 'ucc_adj',
                                  'invreplenishtd': 'inv_replen',
                                  'wavecntrlldodrtd': 'wave_ctrl',
                                  'wavecntrlmboltd': 'wave_ctrl',
                                  'wavecntrlmbolodrtd': 'wave_ctrl',
                                  'ddbooktd': 'dockdoorbkng',
                                  'ddbookintd': 'dockdoorbkng',
                                  'wavecntrlloadtd': 'wave_ctrl',
                                  'wavecntrlwaveodrtd': 'wave_ctrl'
                                };
    const breadcrumbTableMapping = {'asntd': 'mn_asn',
                                    'trtd': 'mn_trade-returns',
                                    'potd' : 'mn_po',
                                    'oxdocktd': 'mn_oxdock',
                                    'sotd': 'mn_shipment_order',
                                    'ixdocktd': 'mn_ixdock',
                                    'shipreftd':'mn_ship_ref',
                                    'transfertd':'mn_vas_trnsfr',
                                    'wotd':'mn_vas_wo',
                                    'inventoryqctd' :'mn_inv_qc',
                                    'stktaketd': 'mn_cycle_count',
                                    'wavetabtd': 'mn_wave_ctrl',
                                    'kittd': 'mn_kitting',
                                    'adjtd': 'mn_inv_adj',
                                    'uccadjtd': 'mn_ucc_adj',
                                    'invreplenishtd': 'mn_inv_replen',
                                    'wavecntrlldodrtd': 'mn_wave_ctrl',
                                    'wavecntrlmboltd': 'mn_wave_ctrl',
                                    'wavecntrlmbolodrtd': 'mn_wave_ctrl',
                                    'ddbooktd': 'mn_dockdoorbkng',
                                    'ddbookintd': 'mn_dockdoorbkng',
                                    'wavecntrlloadtd': 'mn_wave_ctrl',
                                    'wavecntrlwaveodrtd': 'mn_wave_ctrl'
                                   };
    const breadcrumbLabelMap = {
                              'asntd': 'ASN#',
                              'trtd': 'ASN#',
                              'potd' : 'PO#',
                              'oxdocktd': 'ASN#',
                              'sotd': 'SO#',
                              'ixdocktd': 'ASN#',
                              'shipreftd': 'Ship Ref#',
                              'transfertd':'TRF#',
                              'wotd':'WO#',
                              'inventoryqctd' :'IQC#',
                              'stktaketd': 'CC#',
                              'wavetabtd': '',
                              'kittd': 'KIT#',
                              'adjtd': 'AT#',
                              'uccadjtd': 'UCCAT#',
                              'invreplenishtd': '',
                              'wavecntrlldodrtd': '',
                              'wavecntrlmboltd': '',
                              'wavecntrlmbolodrtd': '',
                              'ddbooktd': '',
                              'ddbookintd': '',
                              'wavecntrlloadtd': '',
                              'wavecntrlwaveodrtd': ''
                            };
    const routingMap =  { 
                          'asntd': '/home/inbound/',
                          'trtd': '/home/inbound/',
                          'potd' : '/home/inbound/',
                          'ixdocktd': '/home/inbound/',

                          'oxdocktd': '/home/outbound/',
                          'sotd': '/home/outbound/',
                          'shipreftd': '/home/outbound/',
                          'wavetabtd': '/home/outbound/',
                          'wavecntrlldodrtd': '/home/outbound/',
                          'wavecntrlmboltd': '/home/outbound/',
                          'wavecntrlmbolodrtd': '/home/outbound/',
                          'wavecntrlloadtd': '/home/outbound/',
                          'wavecntrlwaveodrtd': '/home/outbound/',
                          'ddbooktd': '/home/outbound/',
                          'ddbookintd': '/home/outbound/',

                          'transfertd': '/home/inventory/',
                          'wotd':'/home/inventory/',
                          'inventoryqctd' :'/home/inventory/',
                          'stktaketd': '/home/inventory/',
                          'kittd': '/home/inventory/',
                          'adjtd': '/home/inventory/',  
                          'uccadjtd': '/home/inventory/',
                          'invreplenishtd': '/home/inventory/'           
                        }; 
    /*LFWM-2240- LF SCE JReport Integration Phase 2 - wave control screen has different structure, SO handling that seperately
    screenSearchParams has the corresponding screens search params and primarySelectedDocumentId has the primary key of that screen*/
    const saveStateCallback: any = () => {
      let screenSearchParams: any;
      let primarySelectedDocumentId: any;
      let urlTerm: any;
      // WaveId is taken from the  tableSearchServiceForWaveControl.
      if (tableSearchServiceForWaveControl) {
        screenSearchParams = tableSearchServiceForWaveControl.searchParams;
        primarySelectedDocumentId = tableSearchServiceForWaveControl.waveId;
        urlTerm = '/wave-control/';
        // replenishKey is not displayed in the breadcrumb as it is not unique.
      } else {
        screenSearchParams = this.dataService.primarySearchParams;
        if (this.dataService.primarySearchParams.tableId !== 'invreplenishtd') {
          primarySelectedDocumentId = this.dataService.getPrimarySelectedDocument();
          urlTerm = '/details/';
        }
      }
      const breadcrumb = this.resourceService.get(breadcrumbTableMapping[screenSearchParams.tableId]);
      const menuId = menuIdTableMapping[screenSearchParams.tableId];
      const breadcrumbLabel = breadcrumbLabelMap[screenSearchParams.tableId];
      let pmUrl: string = routingMap[screenSearchParams.tableId] + menuIdRouteMappings['jrep_print'];
      const soUrl: string = menuIdRouteMappings[menuId];
      this.sharedServiceRegistry.crossFunctionalityNavigationService
        .sourceDataService = this.dataService;
      // persist whether there were any unsaved changes in SO
      this.sharedServiceRegistry.crossFunctionalityNavigationService
        .hasUnsavedChanges = this.navService.preventNavigation;
      // clear the prevent navigation flag
      this.navService.preventNavigation = false;
      // inventory replenishment breadCrumb is to be non navigatable.
      if (tableSearchServiceForWaveControl || this.dataService.primarySearchParams.tableId !== 'invreplenishtd') {
        this.navService.removeLastBreadCrumbItem().removeLastBreadCrumbItem()
          .addNonNavigableBreadCrumb(breadcrumb);
        this.navService.addBreadCrumbItem(breadcrumbLabel
          + primarySelectedDocumentId,
          soUrl + urlTerm + primarySelectedDocumentId + '?retainState=1');
      }
      this.navService.addNonNavigableBreadCrumb(this.resourceService.get('lbl_jreport') + '#' + data);
      this.navService.navigateByUrl(pmUrl);
    };
    if (this.dataService.displayMode === 'E') { // confirm dialogbox should appear only if it is in edit mode
      this.confirmDialogService.confirmDialog(this.resourceService.get(dialogMsgs))
        .subscribe((isConfirmed: boolean) => {
          if (isConfirmed) {
            saveStateCallback();
          }
        });
    } else {
      saveStateCallback();
    }
  }
  /**
   * this method is use to refresh the accordion after populate po button action
   */
  // [LFWM_2580]- added refreshAccordion to reload the page after save.
  public refreshAccordion() {
    const primarySearchService = this.dataService.primarySearchService;
    primarySearchService
      .getItemDetailForId(primarySearchService.searchParams.idProperty, this.dataService.getPrimarySelectedDocument())
      .subscribe((response: any) => {
        if (!response || !response.cells) {
          this.alertService.error(this.resourceService.get('msg_reload_failure'));
          return;
        }
        response.cells.forEach((cell: any) => {
          cell.isEdited = false;
          this.dataService.primaryDataMap[cell.cName] = cell;
        });
        this.buttonService.initializeDetailPageButtons(this.dataService.primaryDataMap);
      });
  }

  protected createDefaultSearchCondition(id: any) {
    const defaultCondition = new Condition();
    defaultCondition.id = id;
    defaultCondition.displayMode = 'VIEW';
    return defaultCondition;
  }

  protected createLookupParamsForColumn(tableId: any, idProperty: any) {
    const lookupParams = new SearchParams();
    lookupParams.isFullFetch = true;
    lookupParams.columnCount = 10;
    lookupParams.defaultSearchColumn = '';
    lookupParams.tableId = tableId;
    lookupParams.idProperty = idProperty;
    return lookupParams;
  }
  // LFWM-3191- made a new api /withPriv (replacement for setup api)
  //  to pass the privileges for checking the privileges at the backend level.
  public savePageWithPrivilege(privilegeList: any): Observable<any> {
    let entityDetails: Array<any>;
    let lineItems: Array<any>;
    let requestLineItems: Array<any>; // stores the line items to be sent for saving
    let requestEntityItems: Array<any>; // stores the entity details for saving
    let privilege : String;
    let filteredCell : any ;
    if (this.dataService.displayMode === 'C') {
      // set the entity properties
      entityDetails = [];
      const entityData = Object.keys(this.dataService.primaryDataMap);
      entityData.forEach((property: string) => {
        entityDetails.push({ cName: property, cVal: this.dataService.primaryDataMap[property].cVal });
      });
      // Validation of primary id of entity details starts
      if (entityDetails.length !== 0) {
        for (let i = 0; i < entityDetails.length; i++) {
          if ((entityDetails[i].cName === this.dataService.entityConfig.getPrimarySearchParams().idProperty) && !entityDetails[i].cVal) {
            const propertyKey = entityDetails[i].cName;
            const property = this.resourceService.get(this.dataService.primaryMetaDataMap[propertyKey].elementId);
            this.alertService.error(this.dataService.createAlertMessage(this.resourceService.get('err_msg_val_prop'), property));
            return;
          }
        }
      }
      let invalidErrorMsg: any;
      invalidErrorMsg = this.validateEntityDetails(entityDetails);
      if (invalidErrorMsg) {
        this.errorMsg = invalidErrorMsg;
        this.alertService.clearAll().error(this.resourceService.get(this.errorMsg));
        return;
      }
      // Validation of primary id of entity details ends

      entityDetails.push({ cName: 'isNewItem', cVal: true });
      // If there are line items in details page
      if (this.dataService.entityConfig.getShowDetailLineItems()) {
        // set the line item properties
        lineItems = this.dataService.getAllLineItems(this.dataService.primaryDataMap);
      }
    } else if (this.dataService.displayMode === 'E') {
      // set the entity properties
      entityDetails = this.dataService.getModifiedPrimaryProperties(this.dataService.primaryDataMap);
      // Validation of entity details starts
      if (entityDetails.length !== 0) {
        const invalidErrorMsg = this.validateEntityDetails(entityDetails);
        if (invalidErrorMsg) {
          this.errorMsg = invalidErrorMsg;
          this.alertService.clearAll().error(this.resourceService.get(this.errorMsg));
          return;
        }
      }
      // Validation of entity details ends


      // If there are line items in details page
      if (this.dataService.entityConfig.getShowDetailLineItems()) {
        // set the line item properties
        lineItems = this.dataService.getModifiedLineItems(this.dataService.primaryDataMap);
        // adding the warning so as to avoid saving when no modifications are done to the entity and trying to save.
        if (entityDetails.length === 0 && lineItems.length === 0) {
          this.errorMsg = 'msg_no_mod';
          this.alertService.clearAll().warn(this.resourceService.get(this.errorMsg));
          this.dataService.getDetailsSearchService().clearMarkerFlags();
          this.clearEditedFlags();
          this.navService.preventNavigation = false;
          this.dataService.setDisplayMode('V');
          this.buttonService.initializeButtons(this.dataService.primaryDataMap);
          return;
        }
      } else {
        if (entityDetails.length === 0) {
          this.errorMsg = 'msg_no_mod';
          this.alertService.clearAll().warn(this.resourceService.get(this.errorMsg));
          this.clearEditedFlags();
          this.navService.preventNavigation = false;
          this.dataService.setDisplayMode('V');
          this.buttonService.initializeButtons(this.dataService.primaryDataMap);
          return;
        }
      }
    }
    // If there are line items in details page
    if (this.dataService.entityConfig.getShowDetailLineItems()) {
      const errorIfAny = this.validateLineItems(lineItems);
      if (errorIfAny) {
        this.errorMsg = errorIfAny;
        this.alertService.clearAll().error(this.resourceService.get(this.errorMsg));
        return;
      }
    }
    requestEntityItems = this.getEntityValuesList();
    if (this.dataService.entityConfig.getShowDetailLineItems()) {
      requestLineItems = this.getLineItemsList(lineItems);
    }
    // LFWM-3191- In case of dockdoor create button is not present.
    if (this.dataService.primarySearchParams && 
      (this.dataService.primarySearchParams.tableId === 'ddbooktd' || 
       this.dataService.primarySearchParams.tableId === 'ddbookintd')) {
      if (requestLineItems.length === 0) {
        privilege = privilegeList.filter((item: any) => {
          return item.endsWith('_edit');
        });
      } else {
        privilege = privilegeList.filter((item: any) => {
          return item.endsWith('_add');
        });
      }
    } else {
      if (this.dataService.displayMode === 'C') {
        privilege = privilegeList.filter((item: any) => {
          return item.endsWith('_create');
        });
      } else {
        if (requestLineItems && requestLineItems.length > 0) {
          requestLineItems.forEach((row: any) => {
            filteredCell = row.cells.filter((cells: any) => {
              return cells.cName === 'isNewItem' && cells.cVal === true;
            });
          })
          if (filteredCell && filteredCell.length > 0) {
            privilege = privilegeList.filter((item: any) => {
              return item.endsWith('_add');
            });
          } else {
            privilege = privilegeList.filter((item: any) => {
              return item.endsWith('_edit');
            });
          }
        } else {
          privilege = privilegeList.filter((item: any) => {
            return item.endsWith('_edit');
          });
        }
        // LFWM-3191- In some cases add row button is not there(shiprefernce screen)
        if (privilege.length === 0) {
          privilege = privilegeList.filter((item: any) => {
            return item.endsWith('_edit');
          });
        }
      }
    }
    // added to validate the data entered by the user
    if (this.validateUserEnteredData()) {
      if (this.dataService.displayMode === 'C') {
        return this.backendService.saveEntityforAddWithPrivilege(this.dataService.getPrimaryDataIdValue(), requestEntityItems, requestLineItems, privilege[0])
          .map((response) => {
            return response;
          });
      } else if (this.dataService.displayMode === 'E') {
        return this.backendService.saveEntityforUpdateWithPrivilege(this.dataService.getPrimaryDataIdValue(), requestEntityItems, requestLineItems, privilege[0])
          .map((response) => {
            return response;
          });
      }

    }
  }
  // LFWM-3191- made a new api /withPriv (replacement for setup api)
  //  to pass the privileges for checking the privileges at the backend level.
  // [LFWM-2132] - method used to save the edited rows search table
  public saveSearchTableRowsWithPrivilege(privilegeList: any): Observable<any> {
    // to get modified/edited rows from search table
    const lineItems = this.dataService.getModifiedLineItemsOfSearchTable();
    if (lineItems.length === 0) {
      this.errorMsg = 'msg_no_mod';
      this.alertService.clearAll().warn(this.resourceService.get(this.errorMsg));
      this.dataService.getPrimarySearchService().clearMarkerFlags();
      this.navService.preventNavigation = false;
      this.dataService.setDisplayMode('V');
      this.buttonService.initializeSearchPageButtons(this.dataService.primaryDataMap);
      return;
    }
    // format the modified line items in the required request format
    const requestItems = this.getCellValuesListWithPrivilege(lineItems, privilegeList);
    return this.backendService.saveEntityDetailsWithPrivilege(requestItems).map((response) => {
      return response;
    });
  }
  // LFWM-3191- made a new api /withPriv (replacement for setup api)
  //  to pass the privileges for checking the privileges at the backend level.
  // [LFWM-2132] - method to format the line items of search table in required format
  public getCellValuesListWithPrivilege(modifiedLineItems: any, privilegeList: any) {
    const requestList: Array<any> = [];
    let selectedLineItem;
    modifiedLineItems.forEach((row: any, index: any) => {
      selectedLineItem = [];
      const typeName = 'original_';
      row.forEach((cell: any) => {
        if ((cell.cValOrig !== undefined) && (cell.cValOrig !== cell.cVal)) {
          // [LFWM-2408] - no need to send addwho and editWho this will be added at backend
          if (cell.cName.toLowerCase() === 'addwho' || cell.cName.toLowerCase() === 'editwho') {
            return false;
          } else {
          selectedLineItem.push({ cName: typeName + cell.cName, cVal: cell.cValOrig });
          selectedLineItem.push({ cName: cell.cName, cVal: cell.cVal });
          }
        } else {
          // [LFWM-2408] - no need to send addwho and editWho this will be added at backend
          if (cell.cName.toLowerCase() === 'addwho' || cell.cName.toLowerCase() === 'editwho') {
            return false;
          } else {
          selectedLineItem.push({ cName: typeName + cell.cName, cVal: cell.cVal });
          }
        }
      });
      const request = {
        'moduleProperties': selectedLineItem,
        'screenType': this.dataService.primarySearchParams.tableId,
        'privilege': privilegeList[0]
      };
      requestList.push(request);
    });
    return requestList;
  }
  // LFWM-3191- made a new api /withPriv (replacement for setup api)
  //  to pass the privileges for checking the privileges at the backend level.
  // Method used to delete the selected Entity
  public deleteWithPriv(privilege: any) {
    const isNewItem = this.dataService.getPrimarySearchService().getDisplayMode() === 'C'
      && this.dataService.getDetailsSearchService().getDisplayMode() === 'C';
    if (isNewItem) {
      this.alertService.clearAll().success(this.resourceService.get(this.successMsg));
      this.navService.retainAlerts().navigateByUrl(this.dataService.getEntityConfiguration().getSearchPageRoute());
    } else {
      this.confirmDialogService.confirmDialog(this.resourceService.get(this.dialogMsg))
        .subscribe((isConfirmed: any) => {
          if (isConfirmed) {
            const itemNumber = this.dataService.getPrimaryDataIdValue();
            this.backendService.deleteEntityWithPriv(itemNumber, privilege)
              .subscribe((resp: any) => {
                if (resp['statusCode'] && resp['statusMessage']) {
                  if (resp['statusCode'] === 200 && resp['statusMessage'] === 'SUCCESS') {
                    // reload the search page
                    this.alertService.clearAll();
                    this.alertService.success(this.resourceService.get(this.successMsg));
                    this.navService.preventNavigation = false;
                    this.navService.removeLastBreadCrumbItem();
                    this.navService.retainAlerts().navigateByUrl(this.dataService.getEntityConfiguration().getSearchPageRoute());
                  } else if (resp['statusCode'] === 500) {
                    this.alertService.clearAll();
                    this.alertService.error(this.resourceService.translateServerResponse(resp['statusMessage']));
                  }
                } else {
                  if (resp.error) {
                    this.alertService.clearAll();
                    this.alertService.error(this.resourceService.translateServerResponse(resp.error));
                  }
                }
              }, (error) => {
                this.alertService.clearAll().error(this.resourceService.translateServerResponse(error));
              });
          }
        });
    }
  }
  // LFWM-3191- made a new api /withPriv (replacement for setup api)
  //  to pass the privileges for checking the privileges at the backend level.
  // Method used to delete the selected Line Items
  public deleteLineItemWithPriv(privilege: any) {
    const noOfLineItems = this.dataService.getDetailsSearchService().getPagedData().pageDefinition.recordCount;
    const selectedRows = this.dataService.getDetailsSearchService().getSelectedRows();
    if (noOfLineItems === 0) {
      this.errorMsg = 'msg_no_detail';
      this.alertService.clearAll().error(this.resourceService.get(this.errorMsg));
      return;
    }
    const lineItemProp = this.dataService.detailsSearchParams.idProperty;
    if (selectedRows.length > 0) {
      this.confirmDialogService.confirmDialog(this.resourceService.get(this.dialogMsg))
        .subscribe((isConfirmed: any) => {
          if (isConfirmed) {
            const itemNumber = this.dataService.getPrimaryDataIdValue();
            const selectedRow = this.dataService.getDetailsSearchService().getSelectedRows();
            const existingLineItemNumbers: Array<any> = [];
            const newLineItemNumbers: Array<string> = [];
            selectedRow.forEach((row: any) => {
              if (row.cells['isNewItem'] && row.cells['isNewItem'].cVal === true) {
                newLineItemNumbers.push(row.cells[lineItemProp].cVal);
              } else {
                existingLineItemNumbers.push({
                  'value': row.cells[lineItemProp].cVal,
                  'index': row.rIdx
                });
              }
            });
            if (newLineItemNumbers.length > 0) {
              this.dataService.getDetailsSearchService().deleteRowsFromModel(lineItemProp, newLineItemNumbers);
              this.buttonService.initializeButtons(this.dataService.primaryDataMap);
            }
            if (existingLineItemNumbers.length > 0) {
              this.backendService.deleteSelectedLineItemFromEntityWithPriv(itemNumber, existingLineItemNumbers,privilege)
                .subscribe((resp: any) => {
                  if (resp['statusCode'] !== undefined && resp['statusCode'] !== null) {
                    if (resp['statusCode'] === 200 && resp['statusMessage'] === 'SUCCESS') {
                      // code to reload the  detail grid
                      this.alertService.clearAll();
                      this.alertService.success(this.resourceService.get(this.successMsg));
                      this.dataService.reloadDetailTable();
                    } else {
                      this.alertService.clearAll();
                      this.alertService.error(this.resourceService.translateServerResponse(resp.statusMessage));
                    }
                  } else {
                    if (resp.error) {
                      this.alertService.clearAll();
                      this.alertService.error(this.resourceService.translateServerResponse(resp.statusMessage));
                    }
                  }
                }, (error) => {
                  this.alertService.clearAll().error(this.resourceService.translateServerResponse(error));
                });
            }
          }
        });
    } else {
      this.alertService.clearAll();
      this.alertService.error(this.resourceService.get(this.errorMsg));
    }
  }

  // LFWM-3191- made a new api /withPriv (replacement for setup api)
  //  to pass the privileges for checking the privileges at the backend level.
  // Method used to delete all the Line Items
  public deleteAllLineItemsWithPriv(privilege: any) {
    const noOfLineItems = this.dataService.getDetailsSearchService().getPagedData().pageDefinition.recordCount;
    const selectedRow = this.dataService.getDetailsSearchService().getPagedData().pageRows;
    if (noOfLineItems > 0) {
      this.confirmDialogService.confirmDialog(this.resourceService.get(this.dialogMsg))
        .subscribe((isConfirmed: any) => {
          if (isConfirmed) {
            const lineItemProp = this.dataService.detailsSearchParams.idProperty;
            const newLineItemNumbers: Array<string> = [];
            let hasExistingLineItems = false;
            selectedRow.forEach((row: any) => {
              if (row.cells['isNewItem'] && row.cells['isNewItem'].cVal === true) {
                newLineItemNumbers.push(row.cells[lineItemProp].cVal);
              } else {
                hasExistingLineItems = true;
              }
            });

            if (newLineItemNumbers.length > 0) {
              this.dataService.getDetailsSearchService().deleteRowsFromModel(lineItemProp, newLineItemNumbers);
              this.buttonService.initializeButtons(this.dataService.primaryDataMap);
            }

            if (hasExistingLineItems) {
              const entityNumber = this.dataService.getPrimaryDataIdValue();
              this.backendService.deleteAllLineItemsFromEntityWithPriv(entityNumber,privilege)
                .subscribe((resp: any) => {
                  if (resp['statusCode'] !== undefined && resp['statusCode'] !== null) {
                    if (resp['statusCode'] === 200 && resp['statusMessage'] === 'SUCCESS') {
                      // code to reload the detail grid
                      this.alertService.clearAll();
                      this.alertService.success(this.resourceService.get(this.successMsg));
                      this.dataService.reloadDetailTable();
                    } else {
                      this.alertService.clearAll();
                      this.alertService.error(this.resourceService.translateServerResponse(resp.statusMessage));
                    }
                  } else {
                    if (resp.error) {
                      this.alertService.clearAll();
                      this.alertService.error(this.resourceService.translateServerResponse(resp.error));
                    }
                  }
                }, (error) => {
                  this.alertService.clearAll().error(this.resourceService.translateServerResponse(error));
                });
            }
          }
        });
    } else {
      this.alertService.clearAll().error(this.resourceService.get(this.errorMsg));
    }
  }
  // [LFWM-3699] - to get search dynamic buttons based on module name and storerKey in search filter
  public getSearchDynamicButtons(storerKey: any): Observable<any> {
    const moduleName = this.dataService.tableIdToModuleNameMapping[this.dataService.primarySearchParams.tableId];
    const tableId = this.dataService.primarySearchParams.tableId;
    return this.backendService.getDynamicButtons(storerKey, moduleName, tableId).map((resp: any) => {
      return resp;
    });
  }
  // [LFWM-3699] [LFWM-4208] - handling dynamic button action
  public handlingSearchDynamicButtonAction(action: string, storerKeyVal: string) {
    let selectedLineItems = this.dataService.primarySearchService.getSelectedRows();
    // Search dynamic button is only implemented in Trade returns screen and ASN
    //button privilege code in TRADE RETURNS screen is given in the format 'tr_srch_rcm01'. 
    // change the actionCode accordingly while implementing in other screens.

    let actionCode;
    if (this.dataService.primarySearchParams.tableId === 'trtd')
      actionCode = 'TR_SRCH_' + action;
    if(this.dataService.primarySearchParams.tableId === 'asntd')
      actionCode = 'ASN_SRCH_' + action;
    if (selectedLineItems.length === 0) {
      this.errorMsg = 'msg_no_detail';
      this.alertService.clearAll().error(this.resourceService.get(this.errorMsg));
      return;
    }
    let requestList: any = [];
    let successMsgList = [];
    let errorMsgList = [];
    let primaryKey = this.dataService.primarySearchParams.idProperty;
    const currentButtonInfo = this.buttonService.searchdDynamicButtons.filter(button =>
      button.staticButtonName.toUpperCase() === action.toUpperCase());
    const confirmationMsg = currentButtonInfo[0]['confirmationMsg'] ? currentButtonInfo[0]['confirmationMsg'] :
      this.resourceService.get('genric_confirm_msg');
    this.confirmDialogService.confirmDialog(confirmationMsg)
      .subscribe((isConfirmed: any) => {
        if (isConfirmed) {
          selectedLineItems.forEach((row: any) => {
            requestList.push({
              'storerKey': storerKeyVal,
              'documentKey': row.cells[primaryKey].cVal,
              'module': this.dataService.tableIdToModuleNameMapping[this.dataService.primarySearchParams.tableId],
              'code': currentButtonInfo[0]['code'],
              'tableId': this.dataService.primarySearchParams.tableId,
              'screenType': actionCode
            });
          });
          this.backendService.handlingSearchDynamicButtonAction(requestList).subscribe((resp: any) => {
            resp.searchRCMConfigSPList
              .forEach(element => {
                if (element.statusCode === 200) {
                  this.alertService.clearAll();
                  if (element.statusMessage === 'SUCCESS') {
                    successMsgList.push(currentButtonInfo[0]['buttonName'] + ' ' + this.resourceService.get('msg_suc_for') + ' ' + element.documentKey);
                  } else {
                    successMsgList.push(element.statusMessage + element.documentKey);
                  }
                } else if(element.statusCode === 500) {
                  if (element.errMsg !== "") {
                    errorMsgList.push(this.resourceService.translateServerResponse(element.errMsg) + ' ' + element.documentKey);
                  } else {
                    errorMsgList.push(element.statusMessage + ' ' + element.documentKey);
                  }
                }
              });
            if (successMsgList.length > 0) {
              successMsgList.forEach(msg => {
                this.alertService.success(msg);
              });
            }
            if (errorMsgList.length > 0) {
              errorMsgList.forEach(msg => {
                this.alertService.error(msg);
              });
            }
            this.dataService.getPrimarySearchService().performSearch();
          }, (error) => {
            this.alertService.clearAll().error(this.resourceService.get(error));
          });
        }
      });
  }

   
  public copyValueToAllLineItems(searchService: SearchService) {
    let selectedColumnToCopy = '';
    if (searchService.getAllRows().length === 0) {
      this.alertService.clearAll().error(this.resourceService.get(this.errorMsg));
      return false;
    }
    if (searchService.getFocusedRow().cName) {
      selectedColumnToCopy = searchService.getFocusedRow().cName;
    }
    const msgNoFocusRow: string = 'msg_clik_cell_to_copy_value';
    const msgCantCopyField: string = 'msg_can_not_copy_field';

    if (!selectedColumnToCopy || (selectedColumnToCopy === 'isSelected')) {
      this.alertService.clearAll().error(this.resourceService.get(msgNoFocusRow));
      return false;
    }
    const displayTypeSelectedField =
      searchService.getMetadataForProperty(selectedColumnToCopy).displayType;
    //  Non editable field values should not be allowed to copy as they are set from other editable fields
    if (displayTypeSelectedField === 'V') {
      this.alertService.clearAll().error(this.resourceService.get(msgCantCopyField));
      return false;
    }
    let valueToCopy = '';
    if (searchService.getFocusedRow().row) {
      valueToCopy = searchService.getFocusedRow().row.cells[selectedColumnToCopy].cVal;
    }

    this.dialogMsg = this.resourceService.get('msg_copy_colmn') + this.resourceService.get(selectedColumnToCopy)
      + this.resourceService.get('msg_to_all_rows') + this.resourceService.get(selectedColumnToCopy)
      + this.resourceService.get('msg_overwrt');
    this.confirmDialogService.confirmDialog(this.resourceService.get(this.dialogMsg))
      .subscribe((isConfirmed: any) => {
        if (isConfirmed) {
          // needs to check any modified line Items or detail line items are there , if so save then first
          const lineItemCells = searchService.getFocusedRow().row.cells
      let lineNumber;
      let adjustmentKey;
      if(lineItemCells['adjustmentLineNumber']){
        lineNumber = lineItemCells['adjustmentLineNumber'].cVal;
      }
      if(lineItemCells['adjustmentKey']){
        adjustmentKey = lineItemCells['adjustmentKey'].cVal;
      }
      const copyToValue: any = {
        'selectedColumnToCopy': selectedColumnToCopy,
        'valueToCopy': valueToCopy,
        'lineNumber':lineNumber,
        'adjustmentKey':adjustmentKey

      };
      
      // As per Jira LFWM 3648 internally we will save the changes then only we call the new API   
      const receiptDetails = this.dataService.getModifiedPrimaryProperties(this.dataService.primaryDataMap);
      const lineItems = this.dataService.getModifiedLineItems(this.dataService.primaryDataMap);
      if(receiptDetails.length !== 0 || lineItems.length !== 0 ){
        this.savePage().subscribe((responses: any) => {
          let errResponse = responses.find(response => response.statusCode !== 200);
          if (errResponse) {
            this.alertService.clearAll()
              .error(this.resourceService.translateServerResponse(errResponse.statusMessage));
          } else {
            const successMsg = 'msg_save_succ';
            this.refreshAccordion();
            this.copyValueToAllLineItem(copyToValue);
            
          }
        }, (error) => {
          this.alertService.clearAll().error(this.resourceService.translateServerResponse(error));
        });;

      } else {
        this.copyValueToAllLineItem(copyToValue);
      }

        }
      });
  }
   //  LFWM 3648 method to copy Value to all rows 
   public copyValueToAllLineItem(copyToValue: any){
    const request ={
      'tableName': this.dataService.detailsSearchParams.tableId,
      'columnName':copyToValue.selectedColumnToCopy,
      'copyFromKey1':copyToValue.adjustmentKey,
      'copyFromKey2':copyToValue.lineNumber
    }
    this.backendService.saveCopyValueToAllLineItems(request).subscribe((res: any) =>{
      if(res.statusCode === 200 && res.statusMessage === 'SUCCESS'){
        this.alertService.clearAll().success(this.resourceService.get('msg_save_succ'));
        this.dataService.setDisplayMode('V');
         this.dataService.reloadDetailTable();
      }else if(res.statusCode !== 200){
        this.alertService.clearAll()
                .error((res.statusMessage));
        if (this.dataService.detailsSearchService.getNewPageEventInProgress()) {
          this.dataService.detailsSearchService.setNewPageEventInProgress(false);
        }
      }
    })
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/1_SCE_CI_CD_SIT/src/app/shared/services/searchable-entity/detailed-entity/detailed-entity-action.service.ts